import { useState } from "react";

import { BdsIconGiveFeedback, BdsLink, BdsText } from "@buildertrend/components";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";

import { type FeedbackTarget } from "handlers";

import "./FeedbackEmbeddablePanel.less";

interface IFeedbackEmbeddablePanelProps {
    feedbackTarget: FeedbackTarget;
    ratingMode?: "hidden" | "optional" | "required";
    promptText?: string;
    linkText?: string;
    className?: string;
    extraInfo?: string;
}

function FeedbackEmbeddablePanel(props: IFeedbackEmbeddablePanelProps) {
    const {
        feedbackTarget,
        ratingMode = "optional",
        promptText = "We're improving this area and want your feedback. Share what worked, what didn't, or what you expected to see.",
        linkText = "Give feedback",
        className = "margin-bottom-md",
        extraInfo = "",
    } = props;

    const [hideBodyText, setHideBodyText] = useState(false);
    const [hideFeedbackTitle, setHideFeedbackTitle] = useState(false);

    const [, onPanelResize] = useResizeObserver<HTMLDivElement>((resizeEntry) => {
        const width = resizeEntry?.contentRect.width;
        setHideBodyText(width !== undefined ? width < 500 : false);
        setHideFeedbackTitle(width !== undefined ? width < 200 : false);
    });
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleFeedbackSubmitSuccess = () => {
        setIsModalOpen(false);
    };

    const feedbackLink = (
        <BdsLink onClick={() => setIsModalOpen(true)} testid="feedback-embeddable-panel-link">
            {linkText}
        </BdsLink>
    );

    // Standard display (title, body, link)
    // Body text hidden if width is too small
    const standardMessage = (
        <>
            <div className="flex flex-row feedback-panel-message gap-1 justify-between">
                <div className="flex flex-row feedback-panel-main-text gap-1">
                    <div className="flex flex-col">
                        <BdsIconGiveFeedback className="feedback-panel-icon" size="md" />
                    </div>
                    <div className="flex flex-col">
                        <BdsText text="Tell us what you think" size="distinct-sm" />
                        {!hideBodyText && (
                            <>
                                <BdsText text={promptText} className="bds-py-1" size="normal-md" />
                                {feedbackLink}
                            </>
                        )}
                    </div>
                </div>
                {hideBodyText && (
                    <div className="flex flex-col feedback-panel-wrapped-link">{feedbackLink}</div>
                )}
            </div>
        </>
    );

    // Smallest display (link)
    const linkOnlyMessage = (
        <div className="flex flex-row gap-1 justify-between">
            <div className="flex flex-col">{feedbackLink}</div>
        </div>
    );

    let alertMessage: React.ReactNode;
    if (!hideFeedbackTitle) {
        alertMessage = standardMessage;
    } else {
        alertMessage = linkOnlyMessage;
    }

    return (
        <div ref={onPanelResize}>
            <BTAlert
                type="info"
                data-testid="feedback-embeddable-panel-alert"
                message={alertMessage}
                showIcon={false}
                className={className}
            />

            {isModalOpen && (
                <FeedbackWidget
                    feedbackTarget={feedbackTarget}
                    ratingMode={ratingMode}
                    onClose={() => setIsModalOpen(false)}
                    onSubmitSuccess={handleFeedbackSubmitSuccess}
                    extraInfo={extraInfo}
                />
            )}
        </div>
    );
}

export default FeedbackEmbeddablePanel;
